import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  CARER_ROLE,
  COORDINATOR_ROLE,
  CUSTOMER_MEMBER_ROLE,
  FAMILY_ROLE,
  ORGANISATION_ROLES,
  TENANT_ROLES,
} from '@careapp-group/core';
import { MemberDto, PermissionDto } from '@careapp-group/ng-sdk-v2-new';

@Component({
  selector: 'staff-form',
  templateUrl: './staff-form.component.html',
  styleUrls: ['./staff-form.component.scss'],
})
export class StaffFormComponent {
  CARER = CARER_ROLE;
  COORDINATOR_ROLE = COORDINATOR_ROLE;
  ORGANISATION_ROLES = [COORDINATOR_ROLE, CARER_ROLE, CUSTOMER_MEMBER_ROLE, FAMILY_ROLE];
  TENANT_ROLES = TENANT_ROLES;
  protected _disabled = false;
  protected _loading = false;

  userForm: UntypedFormGroup;
  roleForm: UntypedFormGroup;
  model: string;
  permissionId?: string;

  @Output() submitted = new EventEmitter<{ form: any; model?: string; permissionId?: string }>();
  @Input() error: string;
  private _isAdmin: boolean;
  public get isAdmin(): boolean {
    return this._isAdmin;
  }
  @Input()
  public set isAdmin(value: boolean) {
    this._isAdmin = value;
    this.updateRoleForm();
  }

  @Input()
  set disabled(val: boolean) {
    this._disabled = val;
    this.updateRoleForm();
  }

  get disabled() {
    return this._disabled;
  }

  @Input()
  set loading(val: boolean) {
    this._loading = val;
    this.updateRoleForm();
  }
  get loading() {
    return this._loading;
  }

  @Input()
  set staff(value: MemberDto) {
    const emitOptions = { emitEvent: true, onlySelf: true };
    if (value) {
      this.userForm.patchValue(value, emitOptions);
      let permission: PermissionDto;
      try {
        permission = (value.permissions || [])[0];
      } catch (ex) {
        // do nothing
      }
      if (permission) {
        // determines which translations and roles to show to the user.
        this.model = permission.modelName;
        this.permissionId = (permission as any).id;

        // we set different default qualifications depending on the role
        let newPermission = {
          ...permission,
          // Because it's a form group we need the value to not be null/undefined
          qualifications: permission.qualifications || {},
        };
        if (permission.role === COORDINATOR_ROLE) {
          newPermission = {
            ...permission,
            qualifications: {
              ...permission.qualifications,
              accessAllCustomers: permission.qualifications?.accessAllCustomers ?? true,
            },
          };
        }
        this.roleForm.patchValue(newPermission, emitOptions);
      } else {
        this.roleForm.reset();
      }
    } else {
      this.userForm.reset();
    }
  }

  private updateRoleForm() {
    if (this._loading || this._disabled) {
      this.roleForm.disable();
    } else {
      this.roleForm.enable();
    }
    if (this.roleForm.enabled) {
      this.roleForm.get('role').enable();
      this.roleForm.get('enabled').enable();
      this.roleForm.get('externalId').enable();
    }
  }

  constructor(fb: UntypedFormBuilder) {
    this.roleForm = fb.group({
      externalId: [null],
      enabled: [true, Validators.required],
      role: [null, Validators.required],
      qualifications: fb.group({
        accessAllCustomers: [false],
      }),
    });

    this.userForm = fb.group({
      profileImage: ['', Validators.required],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
    });
    // Can only modify role of another user - not their personal details
    this.userForm.disable();
  }

  onSubmit(event) {
    event.stopPropagation();
    event.preventDefault();
    if (this.roleForm.enabled) {
      // Raw value ensures we get the values of disabled inputs
      const payload = this.roleForm.getRawValue();
      if (payload.role !== CARER_ROLE) {
        // Currently carer is the only role that support
        // qualifications, so we clear out any if that role changes to avoid confusion.
        payload.qualifications = {};
      }
      this.submitted.emit({
        form: payload,
        model: this.model,
        permissionId: this.permissionId,
      });
    }
  }
}
